import { TAU, Vec2, arcSweep, normalizeAngle } from './math';
import { nextId } from './ids';
import { entityBBox } from './bbox';
import { intersectEntities } from './query';
import type { ArcEntity, CircleEntity, Entity, LineEntity, PolylineEntity } from './types';

const EPS = 1e-9;
const INTERSECTION_TOLERANCE = 0.01;

function respawn<T extends Entity>(entity: T, patch: Partial<T>): T {
  return { ...entity, ...patch, id: nextId(entity.type.slice(0, 2)) };
}

function toArc(entity: ArcEntity, startAngle: number, endAngle: number): ArcEntity {
  return respawn(entity, { startAngle, endAngle });
}

function withoutArcFields(entity: ArcEntity): CircleEntity {
  return {
    id: entity.id,
    type: 'circle',
    layerId: entity.layerId,
    center: entity.center,
    radius: entity.radius,
    ...(entity.color !== undefined ? { color: entity.color } : {}),
    ...(entity.lineWidth !== undefined ? { lineWidth: entity.lineWidth } : {}),
    ...(entity.linetype !== undefined ? { linetype: entity.linetype } : {}),
  };
}

/** Parameter of `p` projected on the a→b segment (0..1). */
export function paramOnSegment(a: Vec2, b: Vec2, p: Vec2): number {
  const ab = b.sub(a);
  const lenSq = ab.lenSq();
  if (lenSq < 1e-18) return 0;
  return p.sub(a).dot(ab) / lenSq;
}

function intersectionsWith(entity: Entity, others: readonly Entity[]): Vec2[] {
  const points: Vec2[] = [];
  const box = entityBBox(entity);
  for (const other of others) {
    if (other.id === entity.id) continue;
    if (!box.intersects(entityBBox(other))) continue;
    points.push(...intersectEntities(entity, other, { chordTolerance: INTERSECTION_TOLERANCE }));
  }
  return points;
}

/**
 * Removes the portion of `entity` that contains `pickPoint`, bounded by its
 * intersections with the cutters. Returns the replacement entities (0, 1 or 2),
 * or `null` when the pick is not trimmable.
 */
export function trimEntity(
  entity: Entity,
  cutters: readonly Entity[],
  pickPoint: Vec2,
): Entity[] | null {
  if (entity.type === 'line') return trimLine(entity, cutters, pickPoint);
  if (entity.type === 'circle') return trimCircle(entity, cutters, pickPoint);
  if (entity.type === 'arc') return trimArc(entity, cutters, pickPoint);
  return null;
}

function trimLine(entity: LineEntity, cutters: readonly Entity[], pickPoint: Vec2): Entity[] | null {
  const params = [0, 1];
  for (const point of intersectionsWith(entity, cutters)) {
    const t = paramOnSegment(entity.a, entity.b, point);
    if (t > 1e-6 && t < 1 - 1e-6) params.push(t);
  }
  if (params.length === 2) return null;

  params.sort((a, b) => a - b);
  const pickT = paramOnSegment(entity.a, entity.b, pickPoint);
  const at = (t: number): Vec2 => entity.a.lerp(entity.b, t);

  let index = -1;
  for (let i = 0; i + 1 < params.length; i++) {
    if (pickT >= params[i]! - 1e-9 && pickT <= params[i + 1]! + 1e-9) {
      index = i;
      break;
    }
  }
  if (index < 0) return null;

  const pieces: Entity[] = [];
  if (index > 0) {
    pieces.push(respawn(entity, { a: entity.a, b: at(params[index]!) }));
  }
  if (index < params.length - 2) {
    pieces.push(respawn(entity, { a: at(params[index + 1]!), b: entity.b }));
  }
  return pieces;
}

function trimCircle(
  entity: CircleEntity,
  cutters: readonly Entity[],
  pickPoint: Vec2,
): Entity[] | null {
  const angles: number[] = [];
  for (const point of intersectionsWith(entity, cutters)) {
    const angle = normalizeAngle(point.sub(entity.center).angle());
    if (!angles.some((existing) => Math.abs(normalizeAngle(existing - angle)) < 1e-9)) {
      angles.push(angle);
    }
  }
  if (angles.length < 2) return null;
  angles.sort((a, b) => a - b);

  const pickAngle = normalizeAngle(pickPoint.sub(entity.center).angle());
  for (let i = 0; i < angles.length; i++) {
    const start = angles[i]!;
    const end = angles[(i + 1) % angles.length]!;
    const span = i + 1 === angles.length ? normalizeAngle(end - start) : end - start;
    const offset = normalizeAngle(pickAngle - start);
    if (offset <= span + 1e-12) {
      const arc: ArcEntity = {
        id: nextId('a'),
        type: 'arc',
        layerId: entity.layerId,
        center: entity.center,
        radius: entity.radius,
        startAngle: end,
        endAngle: start,
        ccw: true,
        ...(entity.color !== undefined ? { color: entity.color } : {}),
        ...(entity.lineWidth !== undefined ? { lineWidth: entity.lineWidth } : {}),
        ...(entity.linetype !== undefined ? { linetype: entity.linetype } : {}),
      };
      return [arc];
    }
  }
  return null;
}

function trimArc(entity: ArcEntity, cutters: readonly Entity[], pickPoint: Vec2): Entity[] | null {
  const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
  const direction = entity.ccw ? 1 : -1;

  const offsets = [0, sweep];
  for (const point of intersectionsWith(entity, cutters)) {
    const angle = normalizeAngle(point.sub(entity.center).angle());
    const along = entity.ccw
      ? normalizeAngle(angle - entity.startAngle)
      : normalizeAngle(entity.startAngle - angle);
    if (along > 1e-6 && along < sweep - 1e-6) offsets.push(along);
  }
  if (offsets.length === 2) return null;

  offsets.sort((a, b) => a - b);

  const pickAngle = normalizeAngle(pickPoint.sub(entity.center).angle());
  const pickOffset = entity.ccw
    ? normalizeAngle(pickAngle - entity.startAngle)
    : normalizeAngle(entity.startAngle - pickAngle);
  if (pickOffset > sweep) return null;

  let index = -1;
  for (let i = 0; i + 1 < offsets.length; i++) {
    if (pickOffset >= offsets[i]! - 1e-9 && pickOffset <= offsets[i + 1]! + 1e-9) {
      index = i;
      break;
    }
  }
  if (index < 0) return null;

  const angleAt = (offset: number): number => entity.startAngle + direction * offset;
  const pieces: Entity[] = [];
  if (offsets[index]! > 1e-9) pieces.push(toArc(entity, angleAt(0), angleAt(offsets[index]!)));
  if (offsets[index + 1]! < sweep - 1e-9) {
    pieces.push(toArc(entity, angleAt(offsets[index + 1]!), angleAt(sweep)));
  }
  return pieces;
}

/** Extends the end of `entity` nearest to `pickPoint` up to the first boundary. */
export function extendEntity(
  entity: Entity,
  boundaries: readonly Entity[],
  pickPoint: Vec2,
): Entity | null {
  if (entity.type === 'line') return extendLine(entity, boundaries, pickPoint);
  if (entity.type === 'arc') return extendArc(entity, boundaries, pickPoint);
  return null;
}

function probeLength(entity: Entity, boundaries: readonly Entity[]): number {
  let box = entityBBox(entity);
  for (const boundary of boundaries) box = box.union(entityBBox(boundary));
  return Math.max(box.width, box.height, 1) * 8 + 1000;
}

function extendLine(
  entity: LineEntity,
  boundaries: readonly Entity[],
  pickPoint: Vec2,
): Entity | null {
  const length = entity.a.distanceTo(entity.b);
  if (length < EPS) return null;
  const direction = entity.b.sub(entity.a).div(length);
  const atStart = pickPoint.distanceTo(entity.a) <= pickPoint.distanceTo(entity.b);
  const sign = atStart ? -1 : 1;
  const from = atStart ? entity.a : entity.b;

  const probe = {
    id: '__probe__',
    type: 'line' as const,
    layerId: entity.layerId,
    a: from,
    b: from.add(direction.scale(sign * probeLength(entity, boundaries))),
  };

  let best: number | null = null;
  const probeBox = entityBBox(probe);
  for (const boundary of boundaries) {
    if (boundary.id === entity.id) continue;
    if (!probeBox.intersects(entityBBox(boundary))) continue;
    for (const point of intersectEntities(probe, boundary, { chordTolerance: 0.01 })) {
      const distance = point.sub(from).dot(direction) * sign;
      if (distance > 1e-6 && (best === null || distance < best)) best = distance;
    }
  }
  if (best === null) return null;

  const target = from.add(direction.scale(sign * best));
  // Extending modifies the entity in place, so it keeps its identity.
  return atStart ? { ...entity, a: target } : { ...entity, b: target };
}

function extendArc(
  entity: ArcEntity,
  boundaries: readonly Entity[],
  pickPoint: Vec2,
): Entity | null {
  if (entity.radius < EPS) return null;
  const sweep = arcSweep(entity.startAngle, entity.endAngle, entity.ccw);
  const direction = entity.ccw ? 1 : -1;
  const startPoint = entity.center.add(Vec2.polar(entity.radius, entity.startAngle));
  const endPoint = entity.center.add(Vec2.polar(entity.radius, entity.endAngle));
  const atStart = pickPoint.distanceTo(startPoint) <= pickPoint.distanceTo(endPoint);

  const circle = withoutArcFields(entity);
  const circleBox = entityBBox(circle);
  let bestDelta: number | null = null;
  let bestAngle: number | null = null;

  for (const boundary of boundaries) {
    if (boundary.id === entity.id) continue;
    if (!circleBox.intersects(entityBBox(boundary))) continue;
    for (const point of intersectEntities(circle, boundary, { chordTolerance: 0.01 })) {
      const angle = normalizeAngle(point.sub(entity.center).angle());
      const delta = atStart
        ? normalizeAngle(direction * (entity.startAngle - angle))
        : normalizeAngle(direction * (angle - entity.endAngle));
      const limit = TAU - sweep;
      if (delta > 1e-6 && delta < limit - 1e-6 && (bestDelta === null || delta < bestDelta)) {
        bestDelta = delta;
        bestAngle = angle;
      }
    }
  }
  if (bestAngle === null) return null;

  return atStart
    ? { ...entity, startAngle: bestAngle }
    : { ...entity, endAngle: bestAngle };
}

/** Parallel copy of `entity` at `distance`, on the side of `through`. */
export function offsetEntity(entity: Entity, distance: number, through: Vec2): Entity | null {
  const amount = Math.abs(distance);
  if (amount < EPS) return null;

  switch (entity.type) {
    case 'line': {
      const direction = entity.b.sub(entity.a);
      if (direction.len() < EPS) return null;
      const normal = direction.normalize().perp();
      const side = Math.sign(through.sub(entity.a).dot(normal)) || 1;
      const delta = normal.scale(amount * side);
      return respawn(entity, { a: entity.a.add(delta), b: entity.b.add(delta) });
    }
    case 'circle': {
      const inward = through.distanceTo(entity.center) < entity.radius;
      const radius = entity.radius + (inward ? -amount : amount);
      if (radius <= EPS) return null;
      return respawn(entity, { radius });
    }
    case 'arc': {
      const inward = through.distanceTo(entity.center) < entity.radius;
      const radius = entity.radius + (inward ? -amount : amount);
      if (radius <= EPS) return null;
      return respawn(entity, { radius });
    }
    case 'polyline': {
      const vertices = offsetPolyline(entity, amount, through);
      return vertices ? respawn(entity, { vertices }) : null;
    }
    default:
      return null;
  }
}

function intersectInfiniteLines(p1: Vec2, d1: Vec2, p2: Vec2, d2: Vec2): Vec2 | null {
  const denominator = d1.cross(d2);
  if (Math.abs(denominator) < 1e-9) return null;
  const t = p2.sub(p1).cross(d2) / denominator;
  return p1.add(d1.scale(t));
}

function offsetPolyline(entity: PolylineEntity, amount: number, through: Vec2): Vec2[] | null {
  const points = entity.vertices;
  const n = points.length;
  if (n < 2) return null;
  const segmentCount = entity.closed ? n : n - 1;
  if (segmentCount < 1 || (entity.closed && n < 3)) return null;

  let bestDistance = Infinity;
  let side = 1;
  for (let i = 0; i < segmentCount; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % n]!;
    const ab = b.sub(a);
    if (ab.len() < EPS) continue;
    const normal = ab.normalize().perp();
    const t = Math.min(1, Math.max(0, paramOnSegment(a, b, through)));
    const closest = a.lerp(b, t);
    const distance = closest.distanceTo(through);
    if (distance < bestDistance) {
      bestDistance = distance;
      const signed = through.sub(a).dot(normal);
      side = signed >= 0 ? 1 : -1;
    }
  }

  const directions: Vec2[] = [];
  const origins: Vec2[] = [];
  for (let i = 0; i < segmentCount; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % n]!;
    const ab = b.sub(a);
    if (ab.len() < EPS) return null;
    const direction = ab.normalize();
    directions.push(direction);
    origins.push(a.add(direction.perp().scale(amount * side)));
  }

  const result: Vec2[] = [];
  if (!entity.closed) result.push(origins[0]!);

  const jointCount = entity.closed ? segmentCount : segmentCount - 1;
  for (let i = 0; i < jointCount; i++) {
    const previous = i;
    const next = (i + 1) % segmentCount;
    const joint = intersectInfiniteLines(
      origins[previous]!,
      directions[previous]!,
      origins[next]!,
      directions[next]!,
    );
    result.push(joint ?? origins[next]!);
  }

  if (!entity.closed) {
    const last = segmentCount - 1;
    const a = points[last]!;
    const b = points[(last + 1) % n]!;
    result.push(origins[last]!.add(directions[last]!.scale(a.distanceTo(b))));
  }

  return result;
}

/** Reflection of `entity` across the axis through `p1` and `p2`. */
export function mirrorEntityAcross(entity: Entity, p1: Vec2, p2: Vec2): Entity {
  const axis = p2.sub(p1);
  if (axis.len() < EPS) return entity;
  const u = axis.normalize();
  const axisAngle = u.angle();
  const reflect = (p: Vec2): Vec2 => {
    const v = p.sub(p1);
    const along = u.scale(v.dot(u));
    const perpendicular = v.sub(along);
    return p1.add(along).sub(perpendicular);
  };

  switch (entity.type) {
    case 'line':
      return respawn(entity, { a: reflect(entity.a), b: reflect(entity.b) });
    case 'polyline':
      return respawn(entity, { vertices: entity.vertices.map(reflect) });
    case 'circle':
      return respawn(entity, { center: reflect(entity.center) });
    case 'arc':
      return respawn(entity, {
        center: reflect(entity.center),
        startAngle: normalizeAngle(2 * axisAngle - entity.startAngle),
        endAngle: normalizeAngle(2 * axisAngle - entity.endAngle),
        ccw: !entity.ccw,
      });
    case 'ellipse':
      return respawn(entity, {
        center: reflect(entity.center),
        rotation: normalizeAngle(2 * axisAngle - entity.rotation),
      });
    case 'text':
      return respawn(entity, {
        position: reflect(entity.position),
        rotation: normalizeAngle(2 * axisAngle - entity.rotation),
      });
    case 'hatch':
      return respawn(entity, {
        loops: entity.loops.map((loop) => loop.map(reflect)),
        origin: reflect(entity.origin),
        angle: normalizeAngle(2 * axisAngle - entity.angle),
      });
    case 'dimension':
      return respawn(entity, {
        p1: reflect(entity.p1),
        p2: reflect(entity.p2),
        offset: -entity.offset,
      });
    default:
      return entity;
  }
}

// --------------------------------------------------------------- fillet / chamfer

/** What a corner operation produced: the two shortened lines plus the connecting entity. */
export interface CornerResult {
  lines: [LineEntity, LineEntity];
  /** The fillet arc, or the chamfer line. */
  connector: Entity;
  /** Theoretical corner the two lines met at. */
  corner: Vec2;
}

/** Direction of `line` pointing away from `corner`, chosen so `toward` stays on that side. */
function awayFrom(line: LineEntity, corner: Vec2, toward: Vec2): Vec2 | null {
  const direction = line.b.sub(line.a);
  if (direction.len() < EPS) return null;
  const unit = direction.normalize();
  const test = toward.sub(corner).dot(unit);
  const pointing = Math.sign(test) || 1;
  return unit.scale(pointing);
}

/** Keeps the part of `line` that lies on the `direction` side of `corner`. */
function shorten(line: LineEntity, cut: Vec2, direction: Vec2): LineEntity {
  const keepA = line.a.sub(cut).dot(direction) >= 0;
  return respawn(line, keepA ? { a: line.a, b: cut } : { a: cut, b: line.b });
}

/**
 * Fillets the corner between two lines with an arc of `radius`, trimming both to the tangent
 * points. The picks decide which side of the corner survives, exactly like FILLET in CAD.
 */
export function filletLines(
  lineA: LineEntity,
  lineB: LineEntity,
  radius: number,
  pickA: Vec2,
  pickB: Vec2,
): CornerResult | null {
  const r = Math.abs(radius);
  if (r < EPS || lineA.id === lineB.id) return null;

  const corner = intersectInfiniteLines(
    lineA.a,
    lineA.b.sub(lineA.a),
    lineB.a,
    lineB.b.sub(lineB.a),
  );
  if (!corner) return null;

  const dirA = awayFrom(lineA, corner, pickA);
  const dirB = awayFrom(lineB, corner, pickB);
  if (!dirA || !dirB) return null;

  const interior = Math.acos(Math.max(-1, Math.min(1, dirA.dot(dirB))));
  if (interior < 1e-6 || Math.PI - interior < 1e-6) return null;

  const tangent = r / Math.tan(interior / 2);
  const t1 = corner.add(dirA.scale(tangent));
  const t2 = corner.add(dirB.scale(tangent));

  // Never eat more than the line actually has.
  const roomA = Math.max(lineA.a.distanceTo(corner), lineA.b.distanceTo(corner));
  const roomB = Math.max(lineB.a.distanceTo(corner), lineB.b.distanceTo(corner));
  if (tangent > roomA - EPS || tangent > roomB - EPS) return null;

  const bisector = dirA.add(dirB);
  if (bisector.len() < EPS) return null;
  const center = corner.add(bisector.normalize().scale(r / Math.sin(interior / 2)));

  const startAngle = t1.sub(center).angle();
  const endAngle = t2.sub(center).angle();
  const ccw = Math.abs(normalizeAngle(endAngle - startAngle)) < Math.PI;

  const arc: ArcEntity = {
    id: nextId('ar'),
    type: 'arc',
    layerId: lineA.layerId,
    center,
    radius: r,
    startAngle: normalizeAngle(startAngle),
    endAngle: normalizeAngle(endAngle),
    ccw,
  };

  return {
    lines: [shorten(lineA, t1, dirA), shorten(lineB, t2, dirB)],
    connector: arc,
    corner,
  };
}

/**
 * Chamfers the corner between two lines: each side is cut back by its own distance and the gap is
 * closed with a straight line. Picks choose the surviving side, as in `filletLines`.
 */
export function chamferLines(
  lineA: LineEntity,
  lineB: LineEntity,
  distanceA: number,
  distanceB: number,
  pickA: Vec2,
  pickB: Vec2,
): CornerResult | null {
  const dA = Math.abs(distanceA);
  const dB = Math.abs(distanceB);
  if (dA < EPS || dB < EPS || lineA.id === lineB.id) return null;

  const corner = intersectInfiniteLines(
    lineA.a,
    lineA.b.sub(lineA.a),
    lineB.a,
    lineB.b.sub(lineB.a),
  );
  if (!corner) return null;

  const dirA = awayFrom(lineA, corner, pickA);
  const dirB = awayFrom(lineB, corner, pickB);
  if (!dirA || !dirB) return null;
  if (Math.abs(dirA.dot(dirB)) > 1 - 1e-9) return null;

  const roomA = Math.max(lineA.a.distanceTo(corner), lineA.b.distanceTo(corner));
  const roomB = Math.max(lineB.a.distanceTo(corner), lineB.b.distanceTo(corner));
  if (dA > roomA - EPS || dB > roomB - EPS) return null;

  const cutA = corner.add(dirA.scale(dA));
  const cutB = corner.add(dirB.scale(dB));

  return {
    lines: [shorten(lineA, cutA, dirA), shorten(lineB, cutB, dirB)],
    connector: {
      id: nextId('l'),
      type: 'line',
      layerId: lineA.layerId,
      a: cutA,
      b: cutB,
    },
    corner,
  };
}
